import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { X, SlidersHorizontal, ChevronDown, ChevronUp } from 'lucide-react';
import { selectCategories } from '../store/slices/dressesSlice';
import {
  selectFilters,
  setCategory,
  setMinPrice,
  setMaxPrice,
  setSortBy,
  setInStockOnly,
  clearFilters,
} from '../store/slices/filtersSlice';

const SORT_OPTIONS = [
  { value: '-created_at', label: 'Newest First' },
  { value: 'price', label: 'Price: Low to High' },
  { value: '-price', label: 'Price: High to Low' },
  { value: 'name', label: 'Name: A–Z' },
  { value: '-name', label: 'Name: Z–A' },
  { value: '-rating', label: 'Top Rated' },
];

const Section = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center justify-between w-full py-3.5 text-sm font-semibold text-gray-900 hover:text-primary-600 transition-colors"
      >
        {title}
        {open
          ? <ChevronUp className="w-4 h-4 text-gray-400" />
          : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && <div className="pb-4">{children}</div>}
    </div>
  );
};

const FilterSidebar = ({ onClose }) => {
  const dispatch = useDispatch();
  const categories = useSelector(selectCategories);
  const filters = useSelector(selectFilters);

  const hasActiveFilters =
    filters.categoryId !== null ||
    filters.minPrice !== '' ||
    filters.maxPrice !== '' ||
    filters.inStockOnly ||
    filters.sortBy !== '-created_at';

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4 text-primary-600" />
          <h3 className="text-base font-semibold text-gray-900">Filters</h3>
        </div>
        <div className="flex items-center gap-2">
          {hasActiveFilters && (
            <button
              onClick={() => dispatch(clearFilters())}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium px-2 py-1 rounded hover:bg-primary-50 transition-colors"
            >
              Clear All
            </button>
          )}
          {onClose && (
            <button
              onClick={onClose}
              className="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors md:hidden"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Sections */}
      <div className="flex-1 overflow-y-auto px-5">

        {/* Category */}
        {categories.length > 0 && (
          <Section title="Category" defaultOpen={true}>
            <div className="space-y-1">
              <button
                onClick={() => { dispatch(setCategory({ id: null, name: 'All' })); onClose?.(); }}
                className={`flex items-center justify-between w-full px-3 py-2 text-sm rounded-lg transition-colors ${
                  filters.categoryId === null
                    ? 'bg-primary-50 text-primary-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                All Categories
                {filters.categoryId === null && <span className="w-1.5 h-1.5 rounded-full bg-primary-600" />}
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => { dispatch(setCategory({ id: cat.id, name: cat.name })); onClose?.(); }}
                  className={`flex items-center justify-between w-full px-3 py-2 text-sm rounded-lg transition-colors ${
                    filters.categoryId === cat.id
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span>{cat.name}</span>
                  {filters.categoryId === cat.id && <span className="w-1.5 h-1.5 rounded-full bg-primary-600" />}
                </button>
              ))}
            </div>
          </Section>
        )}

        {/* Price Range */}
        <Section title="Price Range" defaultOpen={true}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Min (৳)</label>
                <input
                  type="number"
                  value={filters.minPrice}
                  onChange={e => dispatch(setMinPrice(e.target.value))}
                  placeholder="0"
                  min="0"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Max (৳)</label>
                <input
                  type="number"
                  value={filters.maxPrice}
                  onChange={e => dispatch(setMaxPrice(e.target.value))}
                  placeholder="Any"
                  min="0"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
            {(filters.minPrice !== '' || filters.maxPrice !== '') && (
              <p className="text-xs text-gray-500">
                ৳{filters.minPrice || '0'} – ৳{filters.maxPrice || '∞'}
              </p>
            )}
          </div>
        </Section>

        {/* Sort By */}
        <Section title="Sort By" defaultOpen={true}>
          <div className="space-y-1">
            {SORT_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => dispatch(setSortBy(opt.value))}
                className={`flex items-center justify-between w-full px-3 py-2 text-sm rounded-lg transition-colors ${
                  filters.sortBy === opt.value
                    ? 'bg-primary-50 text-primary-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span>{opt.label}</span>
                {filters.sortBy === opt.value && <span className="w-1.5 h-1.5 rounded-full bg-primary-600" />}
              </button>
            ))}
          </div>
        </Section>

        {/* Availability */}
        <Section title="Availability" defaultOpen={false}>
          <label className="flex items-center gap-3 cursor-pointer group px-1">
            <div className="relative flex-shrink-0">
              <input
                type="checkbox"
                checked={filters.inStockOnly}
                onChange={e => dispatch(setInStockOnly(e.target.checked))}
                className="sr-only peer"
              />
              <div className="w-10 h-5 bg-gray-200 rounded-full peer-checked:bg-primary-600 transition-colors" />
              <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5" />
            </div>
            <span className="text-sm text-gray-700 group-hover:text-gray-900">In Stock Only</span>
          </label>
        </Section>
      </div>
    </div>
  );
};

export default FilterSidebar;
